import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import type { CaptureConfig } from '@photobooth/types';

export function CaptureSettingsPage() {
  const [config, setConfig] = useState<CaptureConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const data = await api.config.getCaptureConfig();
      setConfig(data);
    } catch (err) {
      console.error('Failed to fetch capture config:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    if (config.countdownSeconds < 1 || config.countdownSeconds > 30) {
      alert('Countdown must be between 1 and 30 seconds');
      return;
    }
    if (config.reviewPhotoSeconds < 1 || config.reviewPhotoSeconds > 30) {
      alert('Review photo must be between 1 and 30 seconds');
      return;
    }
    const editSeconds = config.editCountdownSeconds ?? 600;
    if (editSeconds < 1 || editSeconds > 900) {
      alert('Edit countdown must be between 1 and 900 seconds (15 minutes)');
      return;
    }

    setIsSaving(true);
    try {
      await api.config.updateCaptureConfig(config);
      alert('Capture settings updated successfully');
    } catch (err) {
      alert('Failed to update capture settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Failed to load capture configuration
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-text">Capture Settings</h1>
        <p className="text-text-muted mt-1">
          Configure countdown and review timers for all photo booth outlets
        </p>
      </div>

      <div className="bg-surface rounded-lg shadow p-6 max-w-md">
        <h2 className="text-xl font-bold text-text mb-4">Timers</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Countdown (seconds)
            </label>
            <input
              type="number"
              min={1}
              max={30}
              value={config.countdownSeconds}
              onChange={(e) =>
                setConfig({
                  ...config,
                  countdownSeconds: parseInt(e.target.value, 10) || 5,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-sm text-text-muted mt-1">Time before photo is taken (1-30 seconds)</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Review Photo (seconds)
            </label>
            <input
              type="number"
              min={1}
              max={30}
              value={config.reviewPhotoSeconds}
              onChange={(e) =>
                setConfig({
                  ...config,
                  reviewPhotoSeconds: parseInt(e.target.value, 10) || 5,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-sm text-text-muted mt-1">
              Time to review captured photo before next shot (1-30 seconds)
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Edit Countdown (seconds)
            </label>
            <input
              type="number"
              min={1}
              max={900}
              value={config.editCountdownSeconds ?? 600}
              onChange={(e) =>
                setConfig({
                  ...config,
                  editCountdownSeconds: parseInt(e.target.value, 10) || 600,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-sm text-text-muted mt-1">
              Time limit for editing photos in Edit Terminal (1-900 seconds, default 10 min)
            </p>
          </div>
        </div>
        <div className="mt-6">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
